import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminApi } from "./api";
import { shortDate } from "./format";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  X,
  Briefcase,
  ArrowLeft,
  CheckCircle2,
  Circle,
  Save,
} from "lucide-react";
import ExportMenu from "./ExportMenu";

const STATUSES = ["planning", "in_progress", "on_hold", "completed"];
const STATUS_CHIP = {
  planning: "ax-chip",
  in_progress: "ax-chip ax-chip-info",
  on_hold: "ax-chip ax-chip-warn",
  completed: "ax-chip ax-chip-success",
};
const STATUS_LABEL = {
  planning: "Planning",
  in_progress: "In progress",
  on_hold: "On hold",
  completed: "Completed",
};

const EMPTY = {
  client_name: "",
  title: "",
  status: "planning",
  progress: 0,
  start_date: "",
  end_date: "",
  site_address: "",
  notes: "",
  milestones: [
    { label: "Site survey", done: false },
    { label: "Material delivery", done: false },
    { label: "Installation", done: false },
    { label: "Commissioning", done: false },
    { label: "Handover", done: false },
  ],
};

export default function ProjectsModule() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const nav = useNavigate();

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/projects");
      setProjects(r.data?.projects || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return projects.filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (!term) return true;
      return (
        (p.title || "").toLowerCase().includes(term) ||
        (p.client_name || "").toLowerCase().includes(term) ||
        (p.site_address || "").toLowerCase().includes(term)
      );
    });
  }, [projects, q, statusFilter]);

  const onSave = async (data, existing) => {
    try {
      if (existing) {
        await adminApi.put(`/projects/${existing.id}`, data);
        toast.success("Project updated");
      } else {
        await adminApi.post("/projects", data);
        toast.success("Project created");
      }
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete project "${p.title}"?`)) return;
    try {
      await adminApi.delete(`/projects/${p.id}`);
      toast.success("Project deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="p-10 max-w-[1320px] mx-auto" data-testid="projects-module">
      <header className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Briefcase className="w-3.5 h-3.5" style={{ color: "var(--ax-gold)" }} />
            <span className="text-[10.5px] tracking-[0.32em] uppercase" style={{ color: "var(--ax-gold)" }}>
              Projects
            </span>
          </div>
          <h1 className="ax-serif text-5xl tracking-[-0.02em]">Installations</h1>
          <p className="text-sm mt-3" style={{ color: "var(--ax-text-dim)" }}>
            Track every signed quotation from site survey to handover.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportMenu entity="projects" testidPrefix="proj-export" />
          <button type="button" className="ax-btn ax-btn-gold" onClick={() => setEditing("new")} data-testid="proj-add">
            <Plus className="w-4 h-4" /> New project
          </button>
        </div>
      </header>

      <div className="ax-glass mb-5 p-4 flex flex-wrap items-center gap-3">
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg flex-1 min-w-[240px]"
          style={{ background: "rgba(255,255,255,0.02)", border: "1px solid var(--ax-border)" }}
        >
          <Search className="w-4 h-4" style={{ color: "var(--ax-text-muted)" }} />
          <input
            className="bg-transparent flex-1 outline-none text-sm"
            placeholder="Search title, client or site"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="proj-search"
          />
        </div>
        <select
          className="ax-select"
          style={{ width: 180 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="proj-filter-status"
        >
          <option value="all">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <div className="text-xs" style={{ color: "var(--ax-text-muted)" }}>
          {filtered.length} / {projects.length}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="proj-grid">
        {loading && (
          <div className="md:col-span-2 xl:col-span-3 ax-glass p-10 text-center text-sm" style={{ color: "var(--ax-text-muted)" }}>
            Loading…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="md:col-span-2 xl:col-span-3 ax-glass p-10 text-center text-sm" style={{ color: "var(--ax-text-muted)" }}>
            No projects yet. Convert an accepted quotation, or click <span className="ax-chip ax-chip-gold">New project</span>.
          </div>
        )}
        {!loading &&
          filtered.map((p) => {
            const total = (p.milestones || []).length;
            const done = (p.milestones || []).filter((m) => m.done).length;
            const computedProgress =
              typeof p.progress === "number" && p.progress > 0
                ? p.progress
                : total > 0
                ? Math.round((done / total) * 100)
                : 0;
            return (
              <div
                key={p.id}
                className="ax-glass p-5 flex flex-col gap-3"
                data-testid={`proj-card-${p.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{p.title}</div>
                    <div className="text-xs mt-0.5" style={{ color: "var(--ax-text-muted)" }}>
                      {p.client_name}
                    </div>
                  </div>
                  <span className={STATUS_CHIP[p.status] || "ax-chip"}>{STATUS_LABEL[p.status] || p.status}</span>
                </div>

                <div>
                  <div className="flex items-center justify-between text-[10.5px] mb-1.5 tracking-[0.16em] uppercase" style={{ color: "var(--ax-text-dim)" }}>
                    <span>Progress</span>
                    <span className="ax-mono">{computedProgress}%</span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${computedProgress}%`,
                        background: "linear-gradient(90deg, #b89422, #f4d57f)",
                        transition: "width 280ms ease",
                      }}
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  {(p.milestones || []).slice(0, 5).map((m, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs" style={{ color: m.done ? "var(--ax-text)" : "var(--ax-text-dim)" }}>
                      {m.done ? (
                        <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "var(--ax-success)" }} />
                      ) : (
                        <Circle className="w-3.5 h-3.5" />
                      )}
                      <span className={m.done ? "line-through opacity-70" : ""}>{m.label}</span>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-2 mt-auto" style={{ borderTop: "1px solid var(--ax-border)" }}>
                  <div className="text-[10.5px]" style={{ color: "var(--ax-text-muted)" }}>
                    Updated {shortDate(p.updated_at || p.created_at)}
                  </div>
                  <div className="flex items-center gap-1">
                    <button type="button" className="ax-btn ax-btn-ghost px-2" onClick={() => setEditing(p)} data-testid={`proj-edit-${p.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      className="ax-btn ax-btn-ghost ax-btn-danger px-2"
                      onClick={() => onDelete(p)}
                      data-testid={`proj-delete-${p.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {editing && (
        <ProjectDialog
          project={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={onSave}
        />
      )}
    </div>
  );
}

function ProjectDialog({ project, onClose, onSave }) {
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    ...(project || {}),
    milestones: (project?.milestones && project.milestones.length > 0)
      ? project.milestones.map((m) => ({ ...m }))
      : [...EMPTY.milestones],
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleMilestone = (i) =>
    setForm((f) => ({
      ...f,
      milestones: f.milestones.map((m, idx) => (idx === i ? { ...m, done: !m.done } : m)),
    }));
  const renameMilestone = (i, v) =>
    setForm((f) => ({
      ...f,
      milestones: f.milestones.map((m, idx) => (idx === i ? { ...m, label: v } : m)),
    }));
  const addMilestone = () => setForm((f) => ({ ...f, milestones: [...f.milestones, { label: "New milestone", done: false }] }));
  const removeMilestone = (i) => setForm((f) => ({ ...f, milestones: f.milestones.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.client_name.trim()) {
      toast.error("Title and client are required");
      return;
    }
    setSaving(true);
    const total = form.milestones.length;
    const done = form.milestones.filter((m) => m.done).length;
    const payload = {
      ...form,
      progress: total > 0 ? Math.round((done / total) * 100) : Number(form.progress) || 0,
    };
    await onSave(payload, project);
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 ax-scroll overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}
      data-testid="proj-dialog"
    >
      <form onSubmit={submit} className="ax-glass w-full max-w-2xl p-7 relative my-10" style={{ borderRadius: 18 }}>
        <button type="button" className="absolute top-4 right-4 text-white/60 hover:text-white" onClick={onClose} data-testid="proj-dialog-close">
          <X className="w-4 h-4" />
        </button>
        <div className="mb-5">
          <div className="text-[10.5px] tracking-[0.32em] uppercase" style={{ color: "var(--ax-gold)" }}>
            {project ? "Edit" : "New"} project
          </div>
          <h2 className="ax-serif text-3xl mt-1">{project ? project.title : "Kick off installation"}</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="ax-label">Project title *</label>
            <input className="ax-input" value={form.title} onChange={(e) => set("title", e.target.value)} required data-testid="proj-field-title" />
          </div>
          <div>
            <label className="ax-label">Client *</label>
            <input className="ax-input" value={form.client_name} onChange={(e) => set("client_name", e.target.value)} required data-testid="proj-field-client" />
          </div>
          <div>
            <label className="ax-label">Status</label>
            <select className="ax-select" value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="proj-field-status">
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="ax-label">Start date</label>
            <input className="ax-input" type="date" value={(form.start_date || "").slice(0, 10)} onChange={(e) => set("start_date", e.target.value)} data-testid="proj-field-start" />
          </div>
          <div>
            <label className="ax-label">End date</label>
            <input className="ax-input" type="date" value={(form.end_date || "").slice(0, 10)} onChange={(e) => set("end_date", e.target.value)} data-testid="proj-field-end" />
          </div>
          <div className="md:col-span-2">
            <label className="ax-label">Site address</label>
            <input className="ax-input" value={form.site_address || ""} onChange={(e) => set("site_address", e.target.value)} data-testid="proj-field-address" />
          </div>
          <div className="md:col-span-2">
            <label className="ax-label">Notes</label>
            <textarea className="ax-textarea" rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="proj-field-notes" />
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="ax-label !mb-0">Milestones</span>
            <button type="button" className="ax-btn ax-btn-ghost px-2 py-1 text-[11px]" onClick={addMilestone} data-testid="proj-milestone-add">
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>
          <div className="space-y-2">
            {form.milestones.map((m, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`proj-milestone-${i}`}>
                <button
                  type="button"
                  className="shrink-0"
                  onClick={() => toggleMilestone(i)}
                  data-testid={`proj-milestone-toggle-${i}`}
                >
                  {m.done ? (
                    <CheckCircle2 className="w-5 h-5" style={{ color: "var(--ax-success)" }} />
                  ) : (
                    <Circle className="w-5 h-5" style={{ color: "var(--ax-text-muted)" }} />
                  )}
                </button>
                <input
                  className="ax-input flex-1"
                  value={m.label}
                  onChange={(e) => renameMilestone(i, e.target.value)}
                  data-testid={`proj-milestone-label-${i}`}
                />
                <button
                  type="button"
                  className="ax-btn ax-btn-ghost ax-btn-danger px-2"
                  onClick={() => removeMilestone(i)}
                  data-testid={`proj-milestone-remove-${i}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-7 flex items-center justify-end gap-3">
          <button type="button" className="ax-btn ax-btn-ghost" onClick={onClose} data-testid="proj-dialog-cancel">
            Cancel
          </button>
          <button type="submit" className="ax-btn ax-btn-gold" disabled={saving} data-testid="proj-dialog-save">
            <Save className="w-4 h-4" />
            {saving ? "Saving…" : project ? "Save changes" : "Create project"}
          </button>
        </div>
      </form>
    </div>
  );
}
